import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { slideMasters } from '@features/executive-report/utils/slide-masters';
import {
  ShapeProps,
  Slide,
  TextProps,
  TextPropsTextConfig,
} from '@features/powerpoint';
import { cardBackground } from './components/card-background';
import {
  howWeDoItStep1,
  howWeDoItStep2,
  howWeDoItStep3,
} from '@features/executive-report/images';

const CARD_X_START = 0.33;
const CARD_X_OFFSET = 4.28;

type BuildStepTextsProps = {
  index: number;
  title: string;
  description: TextPropsTextConfig[];
  additionalTexts?: TextProps[];
};

const buildStepTexts = (props: BuildStepTextsProps): TextProps[] => {
  return [
    {
      text: [
        {
          text: `Step 0${props.index + 1}`,
          options: {
            fontSize: 21,
            breakLine: true,
          },
        },
        {
          text: props.title,
          options: {
            fontSize: 12,
          },
        },
      ],
      options: {
        color: themeV2.colors.white,
        h: 0.56,
        w: 3.9,
        x: CARD_X_START + props.index * CARD_X_OFFSET,
        y: 2.38,
      },
    },
    {
      text: props.description,
      options: {
        fontSize: 9,
        h: 0.66,
        w: 4.1,
        x: CARD_X_START + props.index * CARD_X_OFFSET,
        y: 2.95,
      },
    },
    ...(props.additionalTexts ?? []),
  ];
};

export const howWeDoItSlide = (): Slide => {
  const steps: Omit<BuildStepTextsProps, 'index'>[] = [
    {
      title: 'Start with what we know',
      description: [],
      additionalTexts: [
        {
          text: 'These shoppers saw your ad',
          options: {
            fontSize: 9.9,
            rotate: 350,
            h: 0.45,
            w: 1.1,
            x: 0.43,
            y: 3.12,
          },
        },
        {
          text: "Here's how we know",
          options: {
            fontSize: 9.9,
            rotate: 7,
            h: 0.45,
            w: 0.77,
            x: 3.65,
            y: 4.17,
          },
        },
        {
          text: 'InMarket receives user-level exposure data.',
          options: {
            fontSize: 7.9,
            h: 0.32,
            w: 1.88,
            x: 1.37,
            y: 5.35,
            align: 'center',
          },
        },
      ],
    },
    {
      title: 'Create a predictive model for exposed users',
      description: [
        {
          text: 'By combining ',
        },
        {
          text: 'historic product sales and media exposure, ',
          options: {
            bold: true,
          },
        },
        {
          text: 'our system learns which shopper patterns ',
        },
        {
          text: ' predict future outcomes. ',
          options: {
            bold: true,
          },
        },
        {
          text: "This is referred to as a 'control'.",
        },
      ],
      additionalTexts: [
        {
          text: 'Training period',
          options: {
            fontSize: 9,
            h: 0.35,
            w: 4.02,
            x: 4.65,
            y: 5.57,
          },
        },
      ],
    },
    {
      title: 'Measure & optimize results',
      description: [
        {
          text: 'By combining this control with ',
        },
        {
          text: 'new exposed data ',
          options: {
            bold: true,
          },
        },
        {
          text: 'the model reveals how your media is affecting ',
        },
        {
          text: 'shoppers ',
          options: {
            bold: true,
          },
        },
        {
          text: 'behavior and driving incremental sales',
        },
      ],
      additionalTexts: [
        {
          text: 'Training period',
          options: {
            fontSize: 9,
            align: 'center',
            h: 0.35,
            w: 1.76,
            x: 9.04,
            y: 5.56,
          },
        },
        {
          text: 'Exposure period',
          options: {
            fontSize: 9,
            align: 'center',
            h: 0.35,
            w: 1.76,
            x: 10.79,
            y: 5.56,
          },
        },
      ],
    },
  ];

  return {
    slideMaster: slideMasters.light,
    color: themeV2.colors.metricBlue,
    images: [
      {
        path: howWeDoItStep1,
        h: 1.97,
        w: 3.26,
        x: 0.79,
        y: 3.19,
      },
      {
        path: howWeDoItStep2,
        h: 1.99,
        w: 3.43,
        x: 4.73,
        y: 3.62,
      },
      {
        path: howWeDoItStep3,
        h: 2.03,
        w: 3.56,
        x: 8.98,
        y: 3.61,
      },
    ],
    shapes: steps.flatMap((_, index): ShapeProps[] => [
      cardBackground({
        h: 3.87,
        w: 4.22,
        x: CARD_X_START + index * CARD_X_OFFSET,
        y: 2.03,
      }),
      {
        shapeName: 'rect',
        options: {
          fill: { type: 'solid', color: themeV2.colors.commandBlue },
          h: 0.63,
          w: 3.83,
          x: CARD_X_START + index * CARD_X_OFFSET,
          y: 2.35,
        },
      },
      {
        shapeName: 'triangle',
        options: {
          fill: { type: 'solid', color: themeV2.colors.commandBlue },
          h: 0.28,
          w: 0.63,
          x: 3.98 + index * CARD_X_OFFSET,
          y: 2.53,
          rotate: 90,
        },
      },
    ]),
    texts: [
      {
        text: 'How We Do It',
        options: {
          fontSize: 36,
          bold: true,
          h: 0.75,
          w: 12.67,
          x: 0.33,
          y: 0.25,
        },
      },
      ...steps.flatMap((step, index) => buildStepTexts({ ...step, index })),
    ],
  };
};
